import React, { useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../common/Flex';
import IconItem from '../../common/icon/IconItem';
import classNames from 'classnames';

// Función auxiliar para calcular cuánto se debería haber gastado
const calculateShouldHaveSpent = (order) => {
  const now = new Date();
  const startDate = new Date(order.dateFrom);
  const endDate = new Date(order.dateTo);
  
  if (now < startDate) return 0;
  if (now > endDate) return order.netInvestment || 0;
  
  const totalDuration = endDate.getTime() - startDate.getTime();
  const elapsedTime = now.getTime() - startDate.getTime();
  const progressPercentage = Math.max(0, Math.min(1, elapsedTime / totalDuration));
  
  return (order.netInvestment || 0) * progressPercentage;
};

const StatsItem = ({ stat }) => {
  const { icon, color, title, amount, suffix, caret, caretColor, target, description } = stat;
  
  return (
    <>
      <Flex
        justifyContent="center"
        alignItems="center"
        className="mb-3 position-static"
      >
        <IconItem
          tag="div"
          icon={icon}
          bg={`${color}-subtle`}
          color={color}
          size="sm"
          iconClass="fs-11"
          className="me-2 shadow-none"
        />
        <h6 className="mb-0 flex-1">{title}</h6>
      </Flex>
      <Flex alignItems="end">
        <div className="flex-1">
          <p className="font-sans-serif lh-1 mb-1 fs-4 fw-bold text-700">
            {amount}
            {suffix && <span className="fs-6 text-muted ms-1">{suffix}</span>}
          </p>
          <div className="d-flex align-items-center">
            {caret && (
              <FontAwesomeIcon
                icon={caret}
                className={`me-1 text-${caretColor} fs-11`}
              />
            )}
            <p className="fs-11 mb-0 text-nowrap text-muted">{target}</p>
          </div>
        </div>
        {description && (
          <div className="text-end">
            <p className="fs-11 text-500 mb-0">{description}</p>
          </div>
        )}
      </Flex>
    </>
  );
};

const PerformanceStats = ({ orders, assignments }) => {
  const stats = useMemo(() => {
    // Calcular métricas principales
    const totalInvestment = orders.reduce((sum, order) => 
      sum + (parseFloat(order.netInvestment) || 0), 0
    );

    const totalAssigned = assignments.reduce((sum, assignment) => {
      let amount = parseFloat(assignment.montoValor) || 0;
      
      // Si es porcentaje, convertir a dólares
      if (assignment.amountType === 'percentage') {
        const order = orders.find(o => o.id === assignment.insertionOrderId);
        if (order && order.netInvestment) {
          amount = (amount * parseFloat(order.netInvestment)) / 100;
        }
      }
      
      return sum + amount;
    }, 0);

    const budgetUtilization = totalInvestment > 0 ? (totalAssigned / totalInvestment) * 100 : 0;

    // Contar órdenes activas
    const now = new Date();
    const activeOrders = orders.filter(order => {
      const startDate = new Date(order.dateFrom);
      const endDate = new Date(order.dateTo);
      return startDate <= now && endDate >= now;
    }).length;

    // Calcular performance score promedio
    const performanceScore = orders.length > 0 ? 
      orders.reduce((sum, order) => {
        const shouldHaveSpent = calculateShouldHaveSpent(order);
        const assigned = assignments
          .filter(a => a.insertionOrderId === order.id)
          .reduce((sum, a) => sum + (parseFloat(a.montoValor) || 0), 0);
        
        const performance = shouldHaveSpent > 0 ? Math.min(100, (assigned / shouldHaveSpent) * 100) : 100;
        return sum + performance;
      }, 0) / orders.length : 0;

    return [
      {
        id: 1,
        title: 'Inversión Total',
        amount: `$${(totalInvestment / 1000).toFixed(1)}`,
        suffix: 'K',
        icon: 'dollar-sign',
        caret: totalInvestment > 0 ? 'caret-up' : null,
        color: 'primary',
        caretColor: 'success',
        target: `${orders.length} órdenes`,
        description: 'Inversión neta'
      },
      {
        id: 2,
        title: 'Presupuesto Utilizado',
        amount: budgetUtilization.toFixed(1),
        suffix: '%',
        icon: 'chart-pie',
        caret: budgetUtilization > 90 ? 'caret-up' : budgetUtilization > 50 ? 'caret-right' : 'caret-down',
        color: budgetUtilization > 110 ? 'danger' : budgetUtilization > 90 ? 'warning' : 'success',
        caretColor: budgetUtilization > 110 ? 'danger' : budgetUtilization > 90 ? 'warning' : 'success',
        target: `$${(totalAssigned / 1000).toFixed(1)}K asignado`,
        description: 'del total'
      },
      {
        id: 3,
        title: 'Órdenes Activas',
        amount: activeOrders,
        icon: 'play-circle',
        caret: activeOrders > 0 ? 'caret-up' : 'caret-down',
        color: 'info',
        caretColor: activeOrders > 0 ? 'success' : 'danger',
        target: `${orders.length - activeOrders} completadas`,
        description: 'en ejecución'
      },
      {
        id: 4,
        title: 'Performance Score',
        amount: performanceScore.toFixed(0),
        suffix: '%',
        icon: 'trophy',
        caret: performanceScore > 80 ? 'caret-up' : performanceScore > 60 ? 'caret-right' : 'caret-down',
        color: performanceScore > 80 ? 'success' : performanceScore > 60 ? 'warning' : 'danger',
        caretColor: performanceScore > 80 ? 'success' : performanceScore > 60 ? 'warning' : 'danger',
        target: 'promedio general',
        description: 'cumplimiento'
      }
    ];
  }, [orders, assignments]);

  return (
    <Card className="mb-3">
      <Card.Body>
        <Row>
          {stats.map((stat, index) => (
            <Col
              lg={3}
              md={6}
              key={stat.id}
              className={classNames({
                'border-bottom border-lg-0 border-lg-end':
                  index !== stats.length - 1 && index !== 1, // No border after last item and after second item on md
                'border-bottom border-md-0 border-md-end':
                  index === 1, // Border after second item on md
                'pb-3 pb-lg-0': index === 0 || index === 1,
                'py-3 py-lg-0': index === 1 || index === 2,
                'pt-3 pt-lg-0': index === 2 || index === 3
              })}
            >
              <StatsItem stat={stat} />
            </Col>
          ))}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default PerformanceStats; 